/**
 * Social CRM Dashboard — simple landing: "pick a client → compose or calendar".
 * One "New post" CTA + a clean client grid with FB/IG connection badges.
 * (The old digest / team workload / unassigned tiles are intentionally gone —
 * backend endpoints for them still exist, this is a pure UI simplification.)
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { clientGroupAPI, integrationAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  PenSquare, CalendarDays, Facebook, Instagram, Loader2, Plus,
  Search, Link2, History,
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;

  const [clients, setClients] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    Promise.all([
      clientGroupAPI.list(wsId).then((r) => r?.data || []).catch(() => []),
      integrationAPI.pages(wsId).then((r) => r?.data?.pages || []).catch(() => []),
    ]).then(([cg, pg]) => {
      setClients(cg);
      setPages(pg);
    }).finally(() => setLoading(false));
  }, [wsId]);

  // client_group_id → {fb: n pages, ig: n IG accounts}
  const connByClient = useMemo(() => {
    const m = {};
    pages.forEach((p) => {
      const cg = p.client_group_id;
      if (!cg) return;
      m[cg] = m[cg] || { fb: 0, ig: 0 };
      m[cg].fb += 1;
      if (p.ig_business_account_id) m[cg].ig += 1;
    });
    return m;
  }, [pages]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? clients.filter((c) => (c.name || '').toLowerCase().includes(q)) : clients;
    // Connected clients first, then alphabetical.
    return [...list].sort((a, b) => {
      const ca = connByClient[a.id] ? 0 : 1;
      const cb = connByClient[b.id] ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [clients, search, connByClient]);

  return (
    <div className="p-6 space-y-6 animate-fade-in-up" data-testid="social-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold">Social CRM</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pick a client, then compose or open their calendar. Facebook + Instagram.
          </p>
        </div>
        <Button onClick={() => navigate('/compose')} data-testid="dashboard-new-post">
          <Plus className="h-4 w-4 mr-2" /> New post
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Find a client..." value={search} onChange={(e) => setSearch(e.target.value)}
               className="pl-9" data-testid="dashboard-client-search" />
      </div>

      {/* Client grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold/50" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="dashboard-empty">
          {clients.length === 0 ? (
            <>No clients yet. Connect Facebook pages and assign them to clients in{' '}
              <button type="button" className="text-gold underline" onClick={() => navigate('/integrations')}>Integrations</button>.</>
          ) : 'No client matches your search.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="dashboard-client-grid">
          {visible.map((c) => {
            const conn = connByClient[c.id];
            return (
              <Card key={c.id} className="group hover:border-gold/40 transition-colors" data-testid={`dashboard-client-${c.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.name || 'Unnamed client'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {conn ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#4d94ff]" data-testid={`dashboard-fb-badge-${c.id}`}>
                              <Facebook className="h-3.5 w-3.5" /> {conn.fb}
                            </span>
                            {conn.ig > 0 && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-pink-300" data-testid={`dashboard-ig-badge-${c.id}`}>
                                <Instagram className="h-3.5 w-3.5" /> {conn.ig}
                              </span>
                            )}
                          </>
                        ) : (
                          <button type="button"
                                  onClick={() => navigate('/integrations')}
                                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-gold"
                                  data-testid={`dashboard-connect-${c.id}`}>
                            <Link2 className="h-3 w-3" /> No accounts connected
                          </button>
                        )}
                      </div>
                    </div>
                    <button type="button" title="Social timeline"
                            onClick={() => navigate(`/clients/${c.id}/social-timeline`)}
                            className="text-muted-foreground hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`dashboard-timeline-${c.id}`}>
                      <History className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 h-8 text-[12px]"
                            onClick={() => navigate(`/compose?client=${c.id}`)}
                            data-testid={`dashboard-compose-${c.id}`}>
                      <PenSquare className="h-3.5 w-3.5 mr-1.5" /> Compose
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 h-8 text-[12px]"
                            onClick={() => navigate(`/clients/${c.id}/content-calendar`)}
                            data-testid={`dashboard-calendar-${c.id}`}>
                      <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Calendar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
